import { DISCOVER_ACTION_TYPES } from './types';
import {
  getFeaturedProfiles,
  getFeaturedProfilesGuest,
  getRecommendedProfiles,
  getTrendingProfiles,
  getTrendingProfilesGuest
} from './userActions';
import { getCreatorLists, getCreatorGuestLists } from './creatorActions';
import { fetchFormattedCategoriesWithCount } from './categoryAction';
import { DEFAULT_USER_AVATAR, DEFAULT_FEATURED_PROFILE_BANNER, ROLE } from '../constants/common';
import { getSignedUrls } from '../utils/fileOperations';
import { getCdnProfileUrl, isUserAdmin } from '../utils/function';

/**
 * Helper function to format profile data
 * @param {Object} profile - Raw profile data from API
 * @returns {Object} Formatted profile object
 */
const formatProfile = (profile) => ({
  _id: profile._id,
  username: '@' + profile.username,
  first_name: profile.first_name,
  last_name: profile.last_name,
  avatar: getCdnProfileUrl(profile.profile_picture, DEFAULT_USER_AVATAR),
  image: getCdnProfileUrl(profile.profile_banner, DEFAULT_FEATURED_PROFILE_BANNER),
  price: profile.rol === ROLE.CREATOR_SUB && profile.price ? profile.price : null,
  rol: profile.rol,
  bio: profile.profile_description || 'No bio available',
  categories: profile.categories || [],
  followers: profile.followers || null,
  isFollowing: profile.isFollowing || false,
  subscribers: profile.subscribers || 0,
  active_promotion: profile?.active_promotion || null,
});

/**
 * Helper function to format trending profile data
 * @param {Object} trendingProfile - Raw trending profile data from API
 * @returns {Object} Formatted trending profile object
 */
const formatTrendingProfile = (trendingProfile) => ({
  _id: trendingProfile?.creator?._id,
  username: '@' + trendingProfile?.creator?.username,
  rol: trendingProfile?.creator?.rol,
  avatar: getCdnProfileUrl(trendingProfile?.creator?.profile_picture, DEFAULT_USER_AVATAR),
  price: trendingProfile?.creator?.price || null,
  isFollowing: trendingProfile?.isFollowing,
  active_promotion: trendingProfile?.active_promotion || null,
  // kind: trendingProfile.kind,
  // count: trendingProfile.count,
  // content_id: trendingProfile.content_id,
  // type_file: trendingProfile.type_file,
  // lock_content: trendingProfile.lock_content,
  // creator: {
  //   _id: trendingProfile._id,
  //   username: '@' + trendingProfile.creator.username,
  //   profile_picture: trendingProfile.creator.profile_picture,
  //   rol: trendingProfile.creator.rol,
  // },
});

/**
 * Helper function to format creator data
 * @param {Object} creator - Raw creator data from API
 * @returns {Object} Formatted creator object
 */
const formatCreator = (creator) => ({
  _id: creator._id,
  username: '@' + creator.username,
  first_name: creator.first_name,
  last_name: creator.last_name,
  avatar: getCdnProfileUrl(creator.profile_picture, DEFAULT_USER_AVATAR),
  price: creator.rol === ROLE.CREATOR_SUB && creator.price ? creator.price : null,
  rol: creator.rol,
  isFollowing: creator.isFollowing || false,
  isSubscribed: creator.isSubscribed || false,
  active_promotion: creator?.active_promotion || null,
  expire_at: creator?.expire_at || null,
  applied_promotion: creator?.applied_promotion || null,
  state: creator?.state || null,
  isFeature: creator?.isFeature || false,
});

/**
 * Fetch Featured Profiles Action
 * @param {boolean} isGuestUser - Whether the user is a guest
 * @returns {Function} Redux thunk action
 */
export const fetchFeaturedProfilesAction = (isGuestUser) => async (dispatch) => {
  if (isUserAdmin()) {
    dispatch({
      type: DISCOVER_ACTION_TYPES.FETCH_FEATURED_PROFILES_SUCCESS,
      payload: [],
    });
    return { success: true, data: [] };
  }

  dispatch({ type: DISCOVER_ACTION_TYPES.FETCH_FEATURED_PROFILES_REQUEST });

  try {
    const response = isGuestUser
      ? await getFeaturedProfilesGuest({ limit: 50 })
      : await getFeaturedProfiles({ limit: 50 });

    if (response.success && response.data) {
      const formattedProfiles = response.data.map(formatProfile);

      dispatch({
        type: DISCOVER_ACTION_TYPES.FETCH_FEATURED_PROFILES_SUCCESS,
        payload: formattedProfiles,
      });

      return { success: true, data: formattedProfiles };
    } else {
      const errorMessage = response.error || 'Failed to fetch featured profiles';
      dispatch({
        type: DISCOVER_ACTION_TYPES.FETCH_FEATURED_PROFILES_FAILURE,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  } catch (error) {
    const errorMessage = error.message || 'Failed to fetch featured profiles';
    dispatch({
      type: DISCOVER_ACTION_TYPES.FETCH_FEATURED_PROFILES_FAILURE,
      payload: errorMessage,
    });

    return { success: false, error: errorMessage };
  }
};

/**
 * Fetch All Creators Action
 * @param {boolean} isGuestUser - Whether the user is a guest
 * @returns {Function} Redux thunk action
 */
export const fetchAllCreatorsAction = (isGuestUser) => async (dispatch) => {
  dispatch({ type: DISCOVER_ACTION_TYPES.FETCH_ALL_CREATORS_REQUEST });

  try {
    const response = isGuestUser
      ? await getCreatorGuestLists({ limit: 9, sortBy: 'createdAt_desc' })
      : await getCreatorLists({ limit: 9, skip: 0, sortBy: 'createdAt_desc' });

    if (response.success && response.data && response.data.data) {
      const creators = response.data.data.map(formatCreator);

      dispatch({
        type: DISCOVER_ACTION_TYPES.FETCH_ALL_CREATORS_SUCCESS,
        payload: creators,
      });

      return { success: true, data: creators };
    } else {
      const errorMessage = response.error || 'Failed to fetch creators';
      dispatch({
        type: DISCOVER_ACTION_TYPES.FETCH_ALL_CREATORS_FAILURE,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  } catch (error) {
    const errorMessage = error.message || 'Failed to fetch creators';
    dispatch({
      type: DISCOVER_ACTION_TYPES.FETCH_ALL_CREATORS_FAILURE,
      payload: errorMessage,
    });

    return { success: false, error: errorMessage };
  }
};

/**
 * Fetch Categories Action
 * @param {boolean} isGuestUser - Whether the user is a guest
 * @returns {Function} Redux thunk action
 */
export const fetchCategoriesAction = (isGuestUser) => async (dispatch) => {
  dispatch({ type: DISCOVER_ACTION_TYPES.FETCH_CATEGORIES_REQUEST });

  try {
    const response = await fetchFormattedCategoriesWithCount(isGuestUser);

    if (response.success && response.data) {
      dispatch({
        type: DISCOVER_ACTION_TYPES.FETCH_CATEGORIES_SUCCESS,
        payload: response.data,
      });

      return { success: true, data: response.data };
    } else {
      const errorMessage = response.error || 'Failed to fetch categories';
      dispatch({
        type: DISCOVER_ACTION_TYPES.FETCH_CATEGORIES_FAILURE,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  } catch (error) {
    const errorMessage = error.message || 'Failed to fetch categories';
    dispatch({
      type: DISCOVER_ACTION_TYPES.FETCH_CATEGORIES_FAILURE,
      payload: errorMessage,
    });

    return { success: false, error: errorMessage };
  }
};

/**
 * Fetch Recommended Profiles Action
 * @param {boolean} isGuestUser - Whether the user is a guest
 * @returns {Function} Redux thunk action
 */
export const fetchRecommendedProfilesAction = (isGuestUser) => async (dispatch) => {
  // Guest users don't have personalized recommendations
  if (isGuestUser || isUserAdmin()) {
    dispatch({
      type: DISCOVER_ACTION_TYPES.FETCH_RECOMMENDED_PROFILES_SUCCESS,
      payload: [],
    });
    return { success: true, data: [] };
  }

  dispatch({ type: DISCOVER_ACTION_TYPES.FETCH_RECOMMENDED_PROFILES_REQUEST });

  try {
    const response = await getRecommendedProfiles({ limit: 9, skip: 0 });

    if (response.success && response.data) {
      const profiles = response.data.map(formatProfile);

      dispatch({
        type: DISCOVER_ACTION_TYPES.FETCH_RECOMMENDED_PROFILES_SUCCESS,
        payload: profiles,
      });

      return { success: true, data: profiles };
    } else {
      const errorMessage = response.error || 'Failed to fetch recommended profiles';
      dispatch({
        type: DISCOVER_ACTION_TYPES.FETCH_RECOMMENDED_PROFILES_FAILURE,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  } catch (error) {
    const errorMessage = error.message || 'Failed to fetch recommended profiles';
    dispatch({
      type: DISCOVER_ACTION_TYPES.FETCH_RECOMMENDED_PROFILES_FAILURE,
      payload: errorMessage,
    });

    return { success: false, error: errorMessage };
  }
};

/**
 * Fetch Trending Profiles Action
 * @param {boolean} isGuestUser - Whether the user is a guest
 * @returns {Function} Redux thunk action
 */
export const fetchTrendingProfilesAction = (isGuestUser) => async (dispatch) => {
  dispatch({ type: DISCOVER_ACTION_TYPES.FETCH_TRENDING_PROFILES_REQUEST });

  try {
    const response = isGuestUser
      ? await getTrendingProfilesGuest({ limit: 10, page: 0 })
      : await getTrendingProfiles({ limit: 10, page: 0 });

    if (response.success && response.data) {
      // Normalize items array from different response shapes
      const items = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data.items)
          ? response.data.items
          : Array.isArray(response.data.data)
            ? response.data.data
            : [];

      // Collect creator avatar keys that need signing
      const avatarKeys = items
        .map((it) => it?.creator?.profile_picture)
        .filter((key) => key && typeof key === 'string' && !key.startsWith('http'));

      let signedUrlMap = {};
      if (avatarKeys.length > 0) {
        try {
          const uniqueKeys = [...new Set(avatarKeys)];
          const signedUrls = await getSignedUrls(uniqueKeys, true);
          signedUrlMap = (signedUrls || []).reduce((acc, cur) => {
            if (cur && cur.key && cur.signedUrl) acc[cur.key] = cur.signedUrl;
            return acc;
          }, {});
        } catch (e) {
          // Fallback silently if signing fails
          signedUrlMap = {};
        }
      }

      const profiles = items.map((it) => {
        const base = formatTrendingProfile(it);
        const rawAvatar = it?.creator?.profile_picture;
        const signedAvatar = rawAvatar && !String(rawAvatar).startsWith('http')
          ? signedUrlMap[rawAvatar]
          : rawAvatar;
        return {
          ...base,
          avatar: signedAvatar || base.avatar || DEFAULT_USER_AVATAR,
        };
      });
      
      dispatch({
        type: DISCOVER_ACTION_TYPES.FETCH_TRENDING_PROFILES_SUCCESS,
        payload: profiles,
      });

      return { success: true, data: profiles };
    } else {
      const errorMessage = response.error || 'Failed to fetch trending profiles';
      dispatch({
        type: DISCOVER_ACTION_TYPES.FETCH_TRENDING_PROFILES_FAILURE,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  } catch (error) {
    const errorMessage = error.message || 'Failed to fetch trending profiles';
    dispatch({
      type: DISCOVER_ACTION_TYPES.FETCH_TRENDING_PROFILES_FAILURE,
      payload: errorMessage,
    });

    return { success: false, error: errorMessage };
  }
};

/**
 * Fetch All Discover Data
 * Fetches all discover screen data in parallel
 * @param {boolean} isGuestUser - Whether the user is a guest
 * @returns {Function} Redux thunk action
 */
export const fetchAllDiscoverData = (isGuestUser) => async (dispatch) => {
  try {
    // Mark the start of data fetching for this auth state
    dispatch({
      type: DISCOVER_ACTION_TYPES.SET_AUTH_STATE,
      payload: { isGuestUser }
    });

    const results = await Promise.allSettled([
      dispatch(fetchFeaturedProfilesAction(isGuestUser)),
      dispatch(fetchAllCreatorsAction(isGuestUser)),
      dispatch(fetchCategoriesAction(isGuestUser)),
      // dispatch(fetchRecommendedProfilesAction(isGuestUser)),
      dispatch(fetchTrendingProfilesAction(isGuestUser)),
    ]);

    // Check if all promises were fulfilled
    const allSuccess = results.every(
      (result) => result.status === 'fulfilled' && result.value?.success !== false
    );

    return {
      success: allSuccess,
      results: results.map((result, index) => ({
        // section: ['featured', 'creators', 'categories', 'recommended', 'trending'][index],
        section: ['featured', 'creators', 'categories', 'trending'][index],
        status: result.status,
        data: result.status === 'fulfilled' ? result.value : null,
        error: result.status === 'rejected' ? result.reason : null,
      })),
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Update Follow Status Action
 * Updates the follow status of a creator in the Redux store
 * @param {string} creatorId - The ID of the creator
 * @param {boolean} isFollowing - Whether the user is now following the creator
 * @returns {Object} Redux action
 */
export const updateFollowStatus = (creatorId, isFollowing) => {
  if (!creatorId) {
    return { type: 'NO_OP' };
  }

  return {
    type: DISCOVER_ACTION_TYPES.UPDATE_FOLLOW_STATUS,
    payload: { creatorId, isFollowing },
  };
};

/**
 * Set Category Page Action
 * Updates the current page index for category pagination
 * @param {number} pageIndex - The page index to set
 * @returns {Object} Redux action
 */
export const setCategoryPage = (pageIndex) => {
  if (typeof pageIndex !== 'number' || pageIndex < 0) {
    return { type: 'NO_OP' };
  }

  return {
    type: DISCOVER_ACTION_TYPES.SET_CATEGORY_PAGE,
    payload: pageIndex,
  };
};

/**
 * Set Recommended Page Action
 * Updates the current page index for recommended profiles pagination
 * @param {number} pageIndex - The page index to set
 * @returns {Object} Redux action
 */
export const setRecommendedPage = (pageIndex) => {
  if (typeof pageIndex !== 'number' || pageIndex < 0) {
    return { type: 'NO_OP' };
  }

  return {
    type: DISCOVER_ACTION_TYPES.SET_RECOMMENDED_PAGE,
    payload: pageIndex,
  };
};

/**
 * Clear Discover Data Action
 * Resets the discover state to initial values
 * @returns {Object} Redux action
 */
export const clearDiscoverData = () => ({
  type: DISCOVER_ACTION_TYPES.CLEAR_DISCOVER_DATA,
});

